import type { Staff } from "@/lib/authv2/store";
import { ACTION } from "@/lib/auth/actions";
import { ROLE } from "@/lib/auth/roles";
import { hasPermission } from "@/lib/authv2/permissions";
import { deptWriteDoorClosed } from "@/lib/auth/dept-door";
import { opsRole } from "@/lib/auth/page-access";

/**
 * Gates for chốt két tiền mặt. These mirror the backend's route guards
 * exactly; where they drift, the UI offers a button the API refuses, which
 * reads to a receptionist as "hệ thống hỏng" rather than "bạn không có quyền".
 */

/**
 * Nhận két / chốt két. The people who physically hold the drawer are lễ tân,
 * and their role (`staff`) carries no action — the backend opens the operate
 * routes with AllowOrDept("cashdrawer.operate", "reception"), so the
 * department check is not a convenience here, it is half the rule.
 */
export function canOperateCashDrawer(staff: Staff | null): boolean {
  if (!staff) return false;
  if (hasPermission(ACTION.CashdrawerOperate)) return true;
  // Same defensive read as page-access.ts: a staff record whose department the
  // API left blank must fail the check, not throw and blank the whole screen.
  // QC / quản lý vùng / chỉ-xem mang phòng lễ tân không giữ két (rà soát
  // 28/09/2026 — máy chủ chặn họ ở cửa phòng ban khi ghi).
  return (staff.department || "").toLowerCase() === "reception" && !deptWriteDoorClosed(staff.role);
}

/** Vai ký được két (CEO chốt 16/09/2026) — bản sao `cashdrawer.CanConfirm`. */
const SIGNER_ROLES = new Set<string>([
  ROLE.Admin,
  ROLE.Director,
  ROLE.BranchManager,
  ROLE.AreaManager,
]);

/**
 * Chữ ký thứ hai. Deliberately NOT open to reception: one person counting the
 * drawer and signing off on their own count is the exact thing this feature
 * exists to prevent.
 *
 * Quyền `cashdrawer.confirm` cấp cho cả vai trưởng phòng, nhưng chỉ trưởng
 * phòng KẾ TOÁN được ký — máy chủ chặn mọi trưởng phòng khác, nên màn cũng
 * không mời họ bấm (kiểm thử toàn vai 16/09/2026, lỗi #11).
 */
export function canConfirmCashSession(staff: Staff | null): boolean {
  if (!staff || !hasPermission(ACTION.CashdrawerConfirm)) return false;
  // Trưởng ca ký như Trưởng cửa hàng (QĐ 0425, CEO 13a) — gương `auth.OpsRole`.
  const role = opsRole(staff).trim().toLowerCase();
  if (SIGNER_ROLES.has(role)) return true;
  return role === ROLE.Manager && (staff.department || "").trim().toLowerCase() === "accounting";
}

/**
 * Người đang đăng nhập có phải người ĐẾM phiên này không. So bằng mã đăng nhập
 * (`casdoorSub`) vì máy chủ lưu người đếm theo mã đó — đem `staff.id` ra so thì
 * không bao giờ khớp. Phiên đếm trước 16/09/2026 không lưu người đếm: coi người
 * nhận két là người đếm, như máy chủ.
 */
export function isCashCounter(
  casdoorSub: string | null | undefined,
  session: { countedById?: string | null; openedById: string },
): boolean {
  if (!casdoorSub) return false;
  return casdoorSub === (session.countedById || session.openedById);
}

/** Vai nộp được tiền vào ngân hàng (CEO chốt 3a, 10/10/2026) — bản sao `cashdrawer.CanRemit`. */
const REMITTER_ROLES = new Set<string>([ROLE.Admin, ROLE.Director, ROLE.BranchManager, ROLE.Accounting]);

/**
 * Nộp tiền mặt vào ngân hàng (không phải tiền cọc): trưởng cửa hàng + kế toán.
 * Quyền `cashdrawer.remit` cấp cho cả vai trưởng phòng, nhưng chỉ trưởng phòng
 * KẾ TOÁN được nộp — máy chủ từ chối mọi trưởng phòng khác, nên màn cũng không
 * mời họ vào thẻ nộp.
 */
export function canRemitCash(staff: Staff | null): boolean {
  if (!staff || !hasPermission(ACTION.CashdrawerRemit)) return false;
  // Trưởng ca nộp như Trưởng cửa hàng (QĐ 0425, CEO 12) — gương `auth.OpsRole`.
  const role = opsRole(staff).trim().toLowerCase();
  if (REMITTER_ROLES.has(role)) return true;
  return role === ROLE.Manager && (staff.department || "").trim().toLowerCase() === "accounting";
}

/** Lịch sử phiên + bảng sức khoẻ két toàn chuỗi. */
export function canReadCashChain(staff: Staff | null): boolean {
  return !!staff && hasPermission(ACTION.CashdrawerRead);
}
